import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { ContainerBreakpoint, ContainerResponsiveValue } from "./contracts/responsive";
import { resolveGapClasses, type Space } from "./foundations/space";

const INLINE_JUSTIFY_CLASSES = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
} as const;

const INLINE_ALIGN_CLASSES = {
  stretch: "items-stretch",
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
} as const;

const INLINE_ROW_CLASSES: Record<ContainerBreakpoint, string> = {
  xs: "@xs:flex-row",
  sm: "@sm:flex-row",
  md: "@md:flex-row",
  lg: "@lg:flex-row",
  xl: "@xl:flex-row",
};

const INLINE_WRAP_CLASSES: Record<ContainerBreakpoint, string> = {
  xs: "@xs:flex-wrap",
  sm: "@sm:flex-wrap",
  md: "@md:flex-wrap",
  lg: "@lg:flex-wrap",
  xl: "@xl:flex-wrap",
};

const INLINE_NOWRAP_CLASSES: Record<ContainerBreakpoint, string> = {
  xs: "@xs:flex-nowrap",
  sm: "@sm:flex-nowrap",
  md: "@md:flex-nowrap",
  lg: "@lg:flex-nowrap",
  xl: "@xl:flex-nowrap",
};

type InlineElement = "div" | "span" | "ul" | "ol" | "nav";
export type InlineJustify = keyof typeof INLINE_JUSTIFY_CLASSES;
export type InlineAlign = keyof typeof INLINE_ALIGN_CLASSES;

export type InlineProps<TElement extends InlineElement = "div"> = {
  children: ReactNode;
  gap?: ContainerResponsiveValue<Space>;
  justify?: InlineJustify;
  align?: InlineAlign;
  wrap?: boolean;
  collapseBelow?: ContainerBreakpoint;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** Wrapping horizontal flow. collapseBelow resolves against an explicit Container ancestor. */
export function Inline<TElement extends InlineElement = "div">({
  align = "stretch",
  as,
  children,
  collapseBelow,
  gap,
  justify = "start",
  ref,
  wrap = true,
  ...nativeProps
}: InlineProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;
  const wrapClass = wrap ? "flex-wrap" : "flex-nowrap";
  const directionClasses = collapseBelow
    ? twJoin(
        "flex-col",
        INLINE_ROW_CLASSES[collapseBelow],
        wrap ? INLINE_WRAP_CLASSES[collapseBelow] : INLINE_NOWRAP_CLASSES[collapseBelow],
      )
    : wrapClass;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin(
        "flex",
        directionClasses,
        INLINE_JUSTIFY_CLASSES[justify],
        INLINE_ALIGN_CLASSES[align],
        resolveGapClasses(gap),
      )}
    >
      {children}
    </Element>
  );
}
